import { useHandler } from "@redotech/react-util/hook";
import classNames from "classnames";
import { memo, ReactNode, useEffect, useState } from "react";
import { RedoFeaturedIconProps } from "./arbiter-components/featured-icon/redo-featured-icon";
import {
  NotificationBreakpoint,
  NotificationButton,
  RedoNotification,
} from "./arbiter-components/notification/redo-notification";
import { Flex } from "./flex";
import * as notificationProviderCss from "./notification-provider.module.css";

export interface NotificationInstance {
  title?: string;
  disabled?: boolean;
  description?: string;
  hideCloseX?: boolean;
  primaryButton?: NotificationButton;
  dismissButton?: Omit<NotificationButton, "onClick">;
  onDismiss?: () => void;
  featuredIcon?: Omit<RedoFeaturedIconProps, "size">;
  fullModalImage?: string;
  breakpoint?: NotificationBreakpoint;
  children?: (dismiss: () => void) => ReactNode;
  dataTestId?: string;
  notificationKey?: string;
  rightOffset?: number;
}

interface NotificationStackItem {
  id: number;
  exiting?: boolean;
  exitHeight?: number;
  instance: NotificationInstance;
  notificationKey?: string;
}

export let enqueueNotification: (
  instance: NotificationInstance,
) => void = () => {
  // cspell:disable-next-line
  console.log("baer");
};

export let popNotification: () => void = () => {
  console.log("pop");
};

export let dismissNotificationByKey: (key: string) => void = () => {
  // No-op until NotificationProvider is mounted
};

export let setNotificationRightOffset: (
  offset: number | undefined,
) => void = () => {
  // No-op until NotificationProvider is mounted
};

const ANIMATION_DURATION_MS = 1500;
const ANIMATION_DURATION_STR = "1.5s";

let notificationId = 0;

/**
 * @deprecated
 *
 * Not actually deprecated, but don't use this anywhere is not already used.
 * It sets a global function, and overriding it will break notifications elsewhere in the app.
 */
export const NotificationProvider = memo(function NotificationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [notifications, setNotifications] = useState<NotificationStackItem[]>(
    [],
  );
  const [globalRightOffset, setGlobalRightOffset] = useState<
    number | undefined
  >(undefined);

  const removeNotification = useHandler((id: number) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  });

  const dismissFirstNotification = useHandler(() => {
    const first = notifications.find((n) => !n.exiting);
    if (first) {
      dismissNotification(first);
    }
  });

  const dismissNotification = useHandler(
    (notification: NotificationStackItem) => {
      notification.instance.onDismiss?.();

      // Get the element to measure its height before starting exit animation
      const element = document.querySelector(
        `[data-notification-id="${notification.id}"]`,
      ) as HTMLElement;
      const currentHeight = element ? element.offsetHeight : 200; // fallback to 200px

      setNotifications((prev) => {
        return prev.map((n) =>
          n.id === notification.id
            ? { ...n, exiting: true, exitHeight: currentHeight }
            : n,
        );
      });
      setTimeout(() => {
        removeNotification(notification.id);
      }, ANIMATION_DURATION_MS);
    },
  );

  const dismissByKey = useHandler((key: string) => {
    const notification = notifications.find(
      (n) => n.notificationKey === key && !n.exiting,
    );
    if (notification) {
      dismissNotification(notification);
    }
  });

  useEffect(() => {
    enqueueNotification = (instance) => {
      const id = notificationId++;
      setNotifications((prev) => [
        ...prev,
        { instance, id, notificationKey: instance.notificationKey },
      ]);
    };

    popNotification = dismissFirstNotification;
    dismissNotificationByKey = dismissByKey;
    setNotificationRightOffset = setGlobalRightOffset;
  }, [dismissFirstNotification, dismissByKey]);

  return (
    <>
      <div
        className={notificationProviderCss.notificationStack}
        style={
          {
            "--animation-duration": ANIMATION_DURATION_STR,
            "--right-offset": (() => {
              const rightValue = globalRightOffset
                ? `${50 + globalRightOffset}px`
                : "50px";
              return rightValue;
            })(),
          } as React.CSSProperties
        }
      >
        {notifications.map((notification, index) => (
          <Flex
            className={classNames(
              notificationProviderCss.notificationContainer,
              notification.exiting && notificationProviderCss.exit,
            )}
            data-notification-id={notification.id}
            key={notification.id}
            style={
              {
                "--stack-index": index,
                "--current-height": notification.exitHeight
                  ? `${notification.exitHeight}px`
                  : "200px",
              } as React.CSSProperties
            }
          >
            <RedoNotification
              {...notification.instance}
              dismiss={() => dismissNotification(notification)}
            />
          </Flex>
        ))}
      </div>
      {children}
    </>
  );
});
